import * as React from 'react';
import { useNavigate, useParams } from "react-router-dom";
import configData from "../config.json";
import { GetSession, GetToken } from '../components/Session';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormDatePicker, FormDropDownList, FormSwitch, FormInput, FormNumericTextBox } from "../components/formComponents";
import { GetPermissions } from '../components/Options';
import { Button } from '@progress/kendo-react-buttons';

const EditInductionResults = () => {
    const { id } = useParams(); // editroles/id url is used to edit existing role, otherwise id is undefined
    const saveName = id === undefined ? "Create" : "Update";
    const [formkey, setFormkey] = React.useState(0); // seems to be needed to push values to form after loading
    const [sites, setSites] = React.useState([]);
    const [permissions, setPermissions] = React.useState(null);
    const navigate = useNavigate();
    const [layout, setLayout] = React.useState([
        { field: 'driver', label: 'Truck/Driver', type: 'text', format: null, isVisible: true },
        { field: 'lastCorrect', label: 'Number Correct', type: 'numeric', format: null, isVisible: true },
        { field: 'lastWrong', label: 'Number Wrong', type: 'numeric', format: null, isVisible: true },
        { field: 'lastCompleted', label: 'Test Time', type: 'date', format: '{0:d/MM/yy hh:mm}', isVisible: true },
        { field: 'lastAnswers', label: 'Last Answers', type: 'text', format: null, isVisible: true },
        { field: 'isActive', label: 'Active', type: 'text', format: null, isVisible: true },
    ]);

    // Gets field from layout based on field name
    const GetField = (f, def = "") => {
        let fld = layout.find(obj => { return obj.field === f });
        if (fld === undefined) return { "field": f, "label": def, "defaultValue": "", "isVisible": true, "type": "text" }
        return fld;
    }

    // State data for form. Values match form value on submit
    const [values, setValues] = React.useState({
        driver: GetField("startDate")["defaultValue"],
        lastCompleted: GetField("displayOrder")["defaultValue"],
        lastCorrect: GetField("repeatPeriod")["defaultValue"],
        lastWrong: GetField("correctAnswerMessage")["defaultValue"],
        lastAnswers: GetField("wrongAnswerMessage")["defaultValue"],
        isActive: String(GetField("isActive")["defaultValue"]).toLowerCase() === "true"
    });

    // When page first loads:
    // - populate drop downs from api/options
    // - if in edit mode, set initial values for all fields for api/drivers
    React.useEffect(() => {
        GetPermissions(["ViewInductionPlaylists", "NewInductionPlaylists", "EditInductionPlaylists"], setPermissions);
        var url = configData.SERVER_URL + "apiv7/userinterfaces?$filter=viewName eq 'editInductionResults'";
        const init = {
            method: 'GET',
            accept: 'application/json',
            headers: { "Authorization": "Bearer " + GetToken() }
        }
        fetch(url, init).then(response => response.json()).then(d => {
            if (GetSession().IsMobile) {
                let data = [];
                for (let i = 0; i < d.length; i++) {
                    data.push({ field: d[i].field, label: d[i].label, type: d[i].type, format: d[i].format, isVisible: d[i].isVisible && d[i].isVisibleOnMobile });
                }
                setLayout(data);
            } else {
                setLayout(d);
            }
        });
        url = configData.SERVER_URL + "apiv7/options";
        fetch(url, init).then(response => response.json()).then(opt => {
            setSites(opt.sites);
            if (id !== undefined) {
                url = `${configData.SERVER_URL}apiv7/inductionresults/${id}`;
                fetch(url, init).then(response => response.json()).then(d => {
                    let dt = d.startDate === null || d.lastCompleted == undefined ? new Date() : new Date(d.lastCompleted + "Z");
                    let st = opt.sites.find(obj => obj.siteId === d.siteId);
                    setValues({
                        driver: d.driver,
                        lastCompleted: dt,
                        lastWrong: d.lastWrong,
                        lastCorrect: d.lastCorrect,
                        lastAnswers: d.lastAnswers,
                        siteId: st,
                        isActive: d.isActive
                    });
                    setFormkey(formkey + 1);
                })
            }
        });
    }, []);

    // Returns value to be sent to web api:
    // If field is visible just return f
    // If field is hidden, return defaultValue from layout data
    const GetDefaultData = (f, name) => {
        let fld = GetField(name);
        if (fld === undefined || fld === null) return f;
        if (fld["isVisible"]) return f;
        let res = "";
        switch (fld["type"]) {
            case "text": res = fld["defaultValue"]; break;
            case "numeric": res = parseFloat(fld["defaultValue"]); break;
            case "boolean": res = fld["defaultValue"] === "true"; break;
            default: res = fld["defaultValue"]; break;
        }
        return res;
    }

    // Handle submit button. For create POST the data, for edit PUT the data, then redirect to offenses index page.
    const handleSubmit = () => {
        let dataItem = { ...values };
        var pth = id === undefined ? "" : "/" + id;
        var st = GetDefaultData(dataItem.siteId, "siteId");
        if (st !== undefined && st.id !== undefined) st = parseInt(st.id);
        var utcDate = dataItem.lastCompleted.toISOString();
        var url = `${configData.SERVER_URL}apiv7/inductionresults${pth}`;
        const init = {
            method: id === undefined ? 'POST' : 'PUT',
            accept: "application/json",
            headers: {
                "Content-type": "application/json",
                "Authorization": "Bearer " + GetToken()
            },
            mode: "cors",
            cache: "no-cache",
            body: JSON.stringify({
                "inductionResultId": id === undefined ? 0 : parseInt(id),
                "driver": GetDefaultData(dataItem.driver, "driver"),
                "lastCompleted": utcDate,
                "siteId": st,
                "lastCorrect": GetDefaultData(dataItem.lastCorrect, "lastCorrect"),
                "lastWrong": GetDefaultData(dataItem.lastWrong, "lastWrong"),
                "lastAnswers": GetDefaultData(dataItem.lastAnswers, "lastAnswers"),
                "isActive": GetDefaultData(dataItem.isActive, "isActive")
            })
        }
        fetch(url, init).then(response => {
            var msg = id === undefined ? "New result created" : "Result updated";
            Redirect(msg);
        });
    }

    // navigation
    const GoBack = () => Redirect("");
    const Redirect = (msg = "") => {
        navigate("/inductionplaylists", { state: { msg: msg } });
    }
    if (permissions && !permissions.CanView) navigate("/error401");;
    if (permissions && !permissions.CanEdit && id !== undefined) navigate("/error401");;
    if (permissions && !permissions.CanCreate && id === undefined) navigate("/error401");;

    const DriverChanged = (e) => { let v = { ...values }; v.driver = e.value; setValues(v) }
    const LastCompletedChanged = (e) => { let v = { ...values }; v.lastCompleted = e.value; setValues(v) }
    const ActiveChanged = (e) => { let v = { ...values }; v.isActive = e.value; setValues(v) }
    const LastCorrectChanged = (e) => { let v = { ...values }; v.lastCorrect = e.value; setValues(v) }
    const LastWrongChanged = (e) => { let v = { ...values }; v.lastWrong = e.value; setValues(v) }
    const SiteChanged = (e) => { let v = { ...values }; v.siteId = e.value; setValues(v) }
    const LastAnswersChanged = (e) => { let v = { ...values }; v.lastAnswers = e.value; setValues(v) }

    // form validators
    const textValidator = (value) => !value ? "Please enter a value" : "";

    return (
        <div>
            <Form initialValues={values} key={formkey} render={(formRenderProps) => (
                <FormElement style={{ width: "98%" }}>
                    <fieldset className={"k-form-fieldset"}>
                        <div className="row">
                            {GetField("driver")["isVisible"] && <div className='col-md-2'>
                                <Field id={"driver"} name={"driver"} label={GetField("driver", "Driver")["label"]} component={FormInput} onChange={DriverChanged} />
                            </div>}
                            {GetField("lastCompleted")["isVisible"] && <div className='col-md-2'>
                                <Field id={"lastCompleted"} name={"lastCompleted"} label={GetField("lastCompleted", "Date")["label"]} component={FormDatePicker} onChange={LastCompletedChanged} />
                            </div>}
                            {GetField("isActive")["isVisible"] && <div className='col-md-3'>
                                <Field id={"isActive"} name={"isActive"} label={GetField("isActive", "Active")["label"]} component={FormSwitch} onChange={ActiveChanged} />
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("lastCorrect")["isVisible"] && <div className='col-md-2'>
                                <Field id={"lastCorrect"} name={"lastCorrect"} label={GetField("lastCorrect", "Number Correct")["label"]} component={FormNumericTextBox} onChange={LastCorrectChanged} />
                            </div>}
                            {GetField("lastWrong")["isVisible"] && <div className="col-md-2">
                                <Field id={"lastWrong"} name={"lastWrong"} label={GetField("lastWrong")["label"], "Number Wrong"} component={FormNumericTextBox} onChange={LastWrongChanged} />
                            </div>}
                            {GetField("siteId")["isVisible"] && <div className='col-md-2'>
                                <Field id={"siteId"} name={"siteId"} label={GetField("siteId", "Site")["label"]} component={FormDropDownList} data={sites} textField={"name"} onChange={SiteChanged} />
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("lastAnswers")["isVisible"] && <div className="col-md-12">
                                <Field id={"lastAnswers"} name={"lastAnswers"} label={GetField("lastAnswers", "Answers")["label"]} component={FormInput} onChange={LastAnswersChanged} />
                            </div>}
                        </div>
                    </fieldset>
                    <div className="k-form-buttons" style={{ paddingTop: 10 }}>
                        <div className='col-md-1'>
                            <Button themeColor={"primary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={handleSubmit}>{saveName}</Button>
                        </div>
                        <div className='col-md-1'>
                            <Button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={GoBack}>Back to List</Button>
                        </div>
                    </div>
                </FormElement>
            )} />

        </div>
    );
}

export default EditInductionResults